import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { OnboardingProductsStore } from '../../../store/store';
import {
  CigoActions,
  UiActions
} from '../../../store/actions';
import { DataClientService } from '../../../services/data-client.service';
import { LoadingService } from '../../../services/loading.service';
import { SessionService } from '../../../services/session.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiResponseStatus, CustomerDocumentType, StepsClientTypes } from '../../../shared/enums';
import { environment } from 'src/environments/environment';
import { AccountFormatService } from 'src/app/services/account-format.service';
import { DatadogService } from 'src/app/services/datadog/datadog.service';
import { RequestCigo, ResponseCigoData } from 'src/app/shared/interfaces/cigo.interface';
import { TagPage } from 'src/app/store/actions/ui.actions';
import { PerformanceData, StaticAssets, StaticAssetsService } from 'src/app/services/static-assets';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-private-landing',
  templateUrl: './private-landing.component.html',
  styleUrls: ['../../../global.scss', './private-landing.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PrivateLandingComponent implements OnInit, OnDestroy {
  isJointTurn: boolean;
  accountName: string;
  formData: FormGroup;
  customerIdentificationNumber: string;
  customerIdentificationType: string;
  sessionDocType: string;
  performanceData: PerformanceData;

  subs = new Subscription();

  cigo$ = this.store.pipe(
    select(OnboardingProductsStore.selectors.cigo)
  );

  constructor(
    private store: Store<OnboardingProductsStore.AppState>,
    private dataDogService: DatadogService,
    private dataClientService: DataClientService,
    private _fb: FormBuilder,
    public loadingService: LoadingService,
    public sessionService: SessionService,
    public accountFormatService: AccountFormatService,
    private staticAssetsService: StaticAssetsService
  ) {
    this.formData = this._fb.group({
      customerJointIdentificationNumber: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
      customerJointIdentificationType: ['DNI', Validators.required],
      customerJointPhoneNumber: ['', [Validators.required, Validators.pattern('^9[0-9]*$')]],
      customerJointEmail: ['', [Validators.required, Validators.email, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$')]],
      isCheckTermsPersonalData: [true, Validators.requiredTrue],
      isCheckTermsCMR: [false]
    });
  }

  cigoSuscription = this.cigo$.subscribe(({ response, error }) => {
    if (!response) {
      return;
    }
    // error case
    if (error !== null) {
      this.store.dispatch(CigoActions.cleanState());
      this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_SERVICE_ERROR }));
    }
    // succes case
    if (response !== null && response.joint &&
      response.joint.operationState.code === ApiResponseStatus.OK &&
      response.client.operationState.code === ApiResponseStatus.OK) {
      if (response.client.operationState.sessionId) {
        localStorage.setItem('sessionId', response.client.operationState.sessionId);
      }
      this.setResponseCigoData(response.joint.data, true);
      this.setResponseCigoData(response.client.data, false);
      this.store.dispatch(CigoActions.cleanState());
      this.dataDogService.addCustomEvent('Landing Complete', environment.mode, { channel: 'client' });
      if (response.joint.data.informacionContacto.direccion) {
        return this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_OFFER }));
      } else {
        return this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_UPDATE_DATA }));
      }
      // Si no es cliente se saca del flujo
    } else if (response !== null && !response.joint?.data) {
      this.store.dispatch(CigoActions.cleanState());
      this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_DERIVATION }));
    }
  });

  // Pasamos por este componente SOLO cuando es un flujo mancomunado por sitios privado.
  ngOnInit() {
    this.customerIdentificationNumber = this.sessionService.customerIdentificationNumber;
    this.sessionDocType = this.sessionService.customerIdentificationType === '1' ? 'DNI' : 'CE';
    localStorage.setItem('sessionId', '1');
    this.store.dispatch(TagPage({ text: 'personal-data-joint' }));
    this.dataDogService.addCustomEvent('Landing Joint Complete', environment.mode, { channel: 'client' });
    this.loadingService.loading = false;
    this.dataClientService.setJointAccountWithoutInfo(false);

    this.subs.add(this.getPerformanceData().subscribe((data) => {
      if (!data.jointState) {
        this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_MAINTENANCE }));
      }
    }));
  }

  ngOnDestroy() {
    this.cigoSuscription.unsubscribe();
  }

  getPerformanceData(): Observable<PerformanceData> {
    return this.staticAssetsService.getStaticResources<PerformanceData>(
      StaticAssets.performanceData
    );
  }

  // Metodo para limpiar el estado luego de un error de servicio.
  actionCleanStateAndRedirect(action) {
    this.store.dispatch(action.cleanState());
    this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_SERVICE_ERROR }));
    return;
  }

  continue() {
    if (this.formData.dirty === false || this.formData.invalid) {
      return;
    }
    this.dataClientService.setIndentificationNumberJoint(this.formData.controls['customerJointIdentificationNumber'].value.toString());
    this.dataClientService.setIdentificationTypeJoint(this.formData.controls['customerJointIdentificationType'].value === 'DNI' ? CustomerDocumentType.DNI : CustomerDocumentType.CE);
    this.dataClientService.setEmailJoint(this.formData.controls['customerJointEmail'].value);
    this.dataClientService.setPhoneNumberJoint(this.formData.controls['customerJointPhoneNumber'].value);
    this.dataClientService.setJointIsForeign(this.formData.controls['customerJointIdentificationType'].value === 'DNI' ? false : true);

    // Data dog set context user
    this.dataDogService.addContextUser(this.sessionService.customerIdentificationNumber);
    this.loadingService.loading = true;

    this.callCigo();
  }

  callCigo() {
    const request: RequestCigo = {
      client: {
        customerIdentificationNumber: this.customerIdentificationNumber,
        customerIdentificationType: this.sessionService.customerIdentificationType
      },
      joint: {
        customerIdentificationNumber: this.dataClientService.getIndentificationNumberJoint(),
        customerIdentificationType: this.dataClientService.getIdentificationTypeJoint()
      }
    };
    return this.store.dispatch(CigoActions.getCigo({ request }));

  }

  setResponseCigoData(response: ResponseCigoData, isJoint: boolean) {
    const email: string = response.informacionContacto.correoElectronico[0].direccionCorreoElectronico;
    const direccion = response.informacionContacto.direccion;
    if (response.informacionContacto && email && direccion) {
      if (isJoint) {
        this.dataClientService.setJointAccountWithoutInfo(true);
        this.dataClientService.setEmailJoint(email);
        this.dataClientService.setCigoDataJoint(response);
      } else {
        this.dataClientService.setEmail(email);
        this.dataClientService.setCigoData(response);
        this.customerIdentificationNumber = response.documentoIdentidad.numeroDocumento;
        this.customerIdentificationType = response.documentoIdentidad.tipoDocumento === CustomerDocumentType.DNI ? 'DNI' : 'CE';
      }
    } else {
      return this.store.dispatch(UiActions.Navigate({ step: StepsClientTypes.STEP_UPDATE_DATA }));
    }
  }
}
